import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner } from '../ui.jsx';

export function Inventory({ toast }) {
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState([]);
  const [techs, setTechs] = useState([]);
  const [tab, setTab] = useState('items');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [transfer, setTransfer] = useState(null);

  const load = () => Promise.all([
    api('/api/inventory/items'),
    api('/api/inventory/stock'),
    api('/api/techs'),
  ]).then(([i,s,t]) => { setItems(i); setStock(s); setTechs(t); })
   .catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async i => {
    if (!confirm(`Delete "${i.name}" and all its stock?`)) return;
    try { await api(`/api/inventory/items/${i.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  const lowStock = items.filter(i => i.lowStock);
  const totalValue = items.reduce((s,i)=>s + (i.inventoryValue||0), 0);

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Inventory</h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn onClick={()=>setTransfer({ itemId:'', fromTechId:'', toTechId:'', quantity:1 })}>↔ Transfer Stock</Btn>
        <Btn v="primary" onClick={()=>setEditing({ unit:'each', reorderPoint:5, reorderQty:10 })}>+ New Item</Btn>
      </div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:18 }}>
      <Stat label="SKUs" value={items.length} sub="active items" color={G.cyan}/>
      <Stat label="Inventory Value" value={fmtUsd(totalValue)} sub="at cost" color={G.green}/>
      <Stat label="Low Stock" value={lowStock.length} sub="need reorder" color={G.red}/>
      <Stat label="Truck Stock" value={stock.filter(s=>s.location==='truck').length} sub="truck SKUs" color={G.purple}/>
    </div>

    {lowStock.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.red+'66', background:G.redLight }}>
      <strong style={{ color:G.red }}>⚠️ Low Stock Alerts ({lowStock.length})</strong>
      <div style={{ fontSize:13, color:G.textMuted, marginTop:6 }}>{lowStock.map(i => `${i.name} (${i.totalStock} left, reorder ${i.reorderQty})`).join(' · ')}</div>
    </div>}

    <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      <Btn sz="sm" v={tab==='items'?'primary':'ghost'} onClick={()=>setTab('items')}>📦 Items</Btn>
      <Btn sz="sm" v={tab==='trucks'?'primary':'ghost'} onClick={()=>setTab('trucks')}>🚐 By Truck</Btn>
    </div>

    {tab === 'items' ? <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['SKU','Item','Category','Stock','Reorder','Cost','Price','Vendor',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{items.map(i => (
          <tr key={i.id} onClick={()=>setEditing(i)} style={{ cursor:'pointer' }}>
            <td style={{ ...styles.td, fontFamily:'monospace', fontSize:12, color:G.textMuted }}>{i.sku||'—'}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{i.name}</td>
            <td style={styles.td}><Badge c={G.cyan} bg={G.cyanLight}>{i.category}</Badge></td>
            <td style={styles.td}>
              <span style={{ color: i.lowStock?G.red:G.text, fontWeight:600 }}>{i.totalStock}</span>
              <span style={{ color:G.textMuted, fontSize:12 }}> {i.unit}</span>
            </td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>at {i.reorderPoint} → order {i.reorderQty}</td>
            <td style={styles.td}>{fmtUsd(i.cost)}</td>
            <td style={styles.td}>{fmtUsd(i.price)}</td>
            <td style={{ ...styles.td, fontSize:12 }}>{i.vendor||'—'}</td>
            <td style={styles.td} onClick={e=>e.stopPropagation()}><Btn sz="xs" v="danger" onClick={()=>remove(i)}>×</Btn></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
    : <div style={{ display:'grid', gridTemplateColumns:`repeat(${techs.length+1}, 1fr)`, gap:12 }}>
      <TruckCol name="🏠 Master Stock" stock={stock.filter(s => s.location==='master')}/>
      {techs.map(t => <TruckCol key={t.id} name={`🚐 ${t.name}`} stock={stock.filter(s => s.techId===t.id)}/>)}
    </div>}

    <ItemModal item={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
    <TransferModal data={transfer} items={items} techs={techs} onClose={()=>setTransfer(null)} onSaved={()=>{ load(); setTransfer(null); toast('Stock transferred'); }}/>
  </div>;
}

function TruckCol({ name, stock }) {
  const total = stock.reduce((s,x)=>s+(x.value||0), 0);
  return <div style={styles.card}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
      <strong style={{ fontSize:14 }}>{name}</strong>
      <Badge c={G.green} bg={G.greenLight}>{fmtUsd(total)}</Badge>
    </div>
    {stock.length === 0 ? <div style={{ fontSize:12, color:G.textMuted, padding:10, textAlign:'center' }}>Empty</div>
    : stock.map(s => (
      <div key={s.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${G.border}`, fontSize:13 }}>
        <span>{s.itemName}</span>
        <strong>{s.quantity}</strong>
      </div>
    ))}
  </div>;
}

function ItemModal({ item, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(item||{}); }, [item]);
  if (!item) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.name) return alert('Name required');
    try {
      if (isNew) await api('/api/inventory/items', { method:'POST', body: f });
      else await api(`/api/inventory/items/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={580}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Inventory Item':f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="SKU"><Input value={f.sku||''} onChange={e=>setF({...f,sku:e.target.value})}/></Field>
      <Field label="Category"><Input value={f.category||''} onChange={e=>setF({...f,category:e.target.value})}/></Field>
      <Field label="Name *" style={{ gridColumn:'1/-1' }}><Input value={f.name||''} onChange={e=>setF({...f,name:e.target.value})}/></Field>
      <Field label="Unit"><Input value={f.unit||'each'} onChange={e=>setF({...f,unit:e.target.value})}/></Field>
      <Field label="Vendor"><Input value={f.vendor||''} onChange={e=>setF({...f,vendor:e.target.value})}/></Field>
      <Field label="Cost ($)"><Input type="number" step="0.01" value={f.cost||0} onChange={e=>setF({...f,cost:Number(e.target.value)})}/></Field>
      <Field label="Sell Price ($)"><Input type="number" step="0.01" value={f.price||0} onChange={e=>setF({...f,price:Number(e.target.value)})}/></Field>
      <Field label="Reorder Point"><Input type="number" value={f.reorderPoint||5} onChange={e=>setF({...f,reorderPoint:Number(e.target.value)})}/></Field>
      <Field label="Reorder Qty"><Input type="number" value={f.reorderQty||10} onChange={e=>setF({...f,reorderQty:Number(e.target.value)})}/></Field>
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function TransferModal({ data, items, techs, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(data||{}); }, [data]);
  if (!data) return null;
  const submit = async () => {
    if (!f.itemId || !f.quantity) return alert('Pick item and quantity');
    try { await api('/api/inventory/transfer', { method:'POST', body: f }); onSaved(); }
    catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={460}>
    <h2 style={{ margin:'0 0 14px 0' }}>Transfer Stock</h2>
    <Field label="Item">
      <Select value={f.itemId||''} onChange={e=>setF({...f,itemId:e.target.value})}>
        <option value="">— select —</option>
        {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
      </Select>
    </Field>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="From">
        <Select value={f.fromTechId||''} onChange={e=>setF({...f,fromTechId:e.target.value||null})}>
          <option value="">Master Stock</option>
          {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </Field>
      <Field label="To">
        <Select value={f.toTechId||''} onChange={e=>setF({...f,toTechId:e.target.value||null})}>
          <option value="">Master Stock</option>
          {techs.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </Field>
    </div>
    <Field label="Quantity"><Input type="number" min={1} value={f.quantity||1} onChange={e=>setF({...f,quantity:Number(e.target.value)})}/></Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Transfer</Btn>
    </div>
  </Modal>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor:color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}
